import type { MouseEventHandler } from 'react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormikHelpers, FormikErrors } from 'formik';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useImmer } from 'use-immer';
import { useSharedRegistryTranslations } from '@apps/registry/SharedRegistry.i18n';
import type { AddCashFundDialogFields, AddCashFundDialogStep0, AddCashFundDialogStep1, AddCashFundDialogStep2, AddCashFundDialogStep3 } from './types';
import { useSpring } from 'react-spring';
import type { OnRemoveRegistryItemHandler } from '@apps/registry/admin/Registry.types';
import { isEmpty } from 'lodash-es';
import globalWindow from '@shared/core/globals';
import type { ProductCatalogCollectionMembersFragment, GetProductCatalogCollectionMembersQuery } from './AddCashFund.graphql.generated';
import type { RegistryCurrencyCodesFragment } from '@graphql/fragments/currency.graphql.generated';
import type { DonationFundFragment } from '@graphql/fragments/registry.graphql.generated';
import type { RegistryItemMetadata } from '@graphql/generated-base';
import type { CreateDonationFundMutationVariables } from '@graphql/mutations/registry.graphql.generated';
import { GetProductCatalogCollectionMembersDocument } from './AddCashFund.graphql.generated';
import { DonationFundPlatformTypeEnum, CustomItemDataType } from '@graphql/generated-base';
import {
  useCreateDonationFundPlatformMutation,
  useCreateDonationFundMutation,
  useUpdateDonationFundMutation,
  useAddDonationFundPhotoMutation,
} from '@graphql/mutations/registry.graphql.generated';
import type { CookedProduct, CookedProductCatalogItem } from '@apps/registry/common/selectors/ProductListSelector';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import {
  getTotalGoalFormatted,
  getMonetaryValueFromMinorUnits,
  getPrefixOffset,
  calculateDonationFundValuesFromFields,
  getInitialAmountAndCurrency,
  DEFAULT_FIXED_AMOUNT,
  DEFAULT_ANY_AMOUNT,
} from './calculationsHelper';
import type { SegmentedControllerItemProps } from '@withjoy/joykit';
import type { PaymentMethodOption } from '@shared/utils/paymentMethodOptions';
import { paymentMethodOptions } from '@shared/utils/paymentMethodOptions';
import { useAddCashFundTelemetry } from './AddCashFund.telemetry';
import type { CatalogDepartmentDictionaryEntry } from '../Catalog/state/Catalog/Catalog.types';
import { useStoredCuration } from '../Catalog/hooks/useStoredCuration';
import { useCreateAddRegistryItemClickPayload } from '../Catalog/utils/createTelemetryPayload';
import type { AddRegistryMethodType } from '../Catalog/Catalog.telemetry.types';
import { deriveTelemetryPage } from '../Catalog/utils/deriveTelemetryPage';
import { getCurrencySymbolFromCurrencyCode } from '@shared/utils/currency/utils';
import { useSyncRegistryCurrencyToForm } from './useSyncRegistryCurrencyToForm';
import { useResolvedCurrencyCode } from '@shared/hooks/useResolvedCurrencyCode';
import { isUsdCurrencyCode } from './AddCashFund.constants';
import type { DonationFundInfoFragmentWithUrl } from '../Catalog/components/ProductGrid/ProductGrid.controller';
import { useTheme } from '@withjoy/joykit';
import { createPaymentMetadata, findDonationFundPlatform, getPaymentLink, normalizePaymentUsername } from '@apps/registry/common/components/AddCashFund/helpers';
import { useIdentify } from '@shared/core';
import { useApolloClient } from '@apollo/client';
import { useAdminRegistryTelemetry } from '@apps/registry/admin/AdminRegistry.telemetry';
import { useEventInfo } from '@shared/utils/eventInfo';
import { withWindow } from '@shared/utils/withWindow';
import { generateRegistryItemMetadataSourceFromCatalog } from '../Catalog/utils/registryItemMetadata';
import { RegistryItemSource } from '../Catalog/Catalog.constants';
import { useAuth } from '@shared/components/AuthProvider';
import { useAppPaymentDisclaimerDialogController } from './AppPaymentDisclaimerDialog.controller';
import { useCreditCardForCashEligibility } from '@apps/registry/admin/components/CreditCardSetup/useCreditCardForCashEligibility';
import { useCreditCardSetupContext } from '@apps/registry/admin/components/CreditCardSetup/CreditCardSetUp.context';
import { useShippingAddressAndCurrencyDialogsContext } from '@apps/registry/common/components/ShippingAddressAndCurrencyDialogsProvider';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { ensureHttpsPrefix, isValidUrlAndDomain } from '@shared/utils/urlHelpers';
import { addError } from '@shared/utils/logger';

interface AddCashFundControllerProps {
  dialogShown?: boolean;
  eventId: string;
  hideDialog: () => void;
  photoFilename?: string;
  product?: CookedProduct | CookedProductCatalogItem | null;
  updatedPhoto?: string;
  isEditDialog?: boolean;
  donationFundInfo?: DonationFundInfoFragmentWithUrl;
  openFilepicker: () => void;
  fundType: CustomItemDataType.cash | CustomItemDataType.charity;
  isCharityFund: boolean;
  isCatalogDialog?: boolean;
  isNewCashFund: boolean;
  selectedCategory: Maybe<CatalogDepartmentDictionaryEntry>;
  addMethodType: AddRegistryMethodType;
  itemPosition: Maybe<number>;
  registryCurrency?: RegistryCurrencyCodesFragment | null;
  handleOnRemove: OnRemoveRegistryItemHandler;
  telemCategoryOverride?: string;
}

interface FormValues
  extends Readonly<{
    name: string;
    qty: number;
    amount: number | undefined;
    total: number;
    paymentMethod: Maybe<DonationFundPlatformTypeEnum>;
    username: string;
    description: string;
    image: string | undefined;
    currency: string;
    currencyCode: string;
    charityLink: string;
    enableCashOrCheck: boolean;
    enableJoyCredit: boolean;
    enableAppPlatform: boolean;
  }> {}

type State = {
  currentStep: number;
  reviewStep: number | null;
  formValues: FormValues;
  amountPrefixOffset: number;
  totalPrefixOffset: number;
  isNameFieldFocused: boolean;
  namePlaceholderText: string;
  isDeleting: boolean;
  speed: number;
  loop: number;
  isSuccessStep: boolean;
  isCancelClicked: boolean;
  isLinkMousedOver: boolean;
  totalGoalErrorMessage: string | undefined;
  isPaymentMethodWarningShown: boolean;
  isLinkToCharitySelected: boolean;
  isAnyAmountSelected: boolean;
  isGoalHidden: boolean;
  showCatalogCompletionStep: boolean;
};

type DialogTimers = {
  resetTriggerTimeout: number | undefined;
  placeholderAnimationTimeout: number | undefined;
  placeholderDeletingTimeout: number | undefined;
  closeDialogTimeout: number | undefined;
  resetFormTimeout: number | undefined;
  hideCatalogCompletion: number | undefined;
};

const NOTE_MAX_CHARS = 512;

const notEmailRegexp = /^[^@]+$/g;

const AT_LEAST_ONE_METHOD_TEST: Yup.TestOptions = {
  name: 'at-least-one-true',
  exclusive: true,
  message: 'You must select at least one contribution method',
  test: function () {
    const values = this.parent;
    return values.isLinkToCharitySelected || values.enableJoyCredit || values.enableCashOrCheck || values.enableAppPlatform;
  },
};

export const useAddCashFundDialogController = ({
  dialogShown,
  eventId,
  hideDialog,
  photoFilename,
  product,
  updatedPhoto,
  isEditDialog,
  donationFundInfo,
  openFilepicker,
  isCatalogDialog,
  fundType,
  isCharityFund,
  isNewCashFund,
  selectedCategory,
  addMethodType,
  itemPosition,
  registryCurrency,
  handleOnRemove,
  telemCategoryOverride,
}: AddCashFundControllerProps) => {
  const {
    cashFundDialogDetails,
    honeymoonFundSuccessView,
    onChangeCashFundDialogState,
    onCashFundDialogFieldEdit,
    onAddToRegistryClick,
    onSaveRegistryItem,
    onHoneymoonRemindMeLater,
    onHoneymoonStartExploring,
    onChangeCurrencyCtaClicked,
  } = useAddCashFundTelemetry(eventId, telemCategoryOverride);
  const { enableMustHave: trackEnableMustHave, clickViewRegistry } = useAdminRegistryTelemetry();

  const creditCardSetupContext = useCreditCardSetupContext(false);
  const payoutAccountInfo = creditCardSetupContext?.payoutAccountInfo ?? null;
  const fetchingPayoutInfo = creditCardSetupContext?.fetchingPayoutInfo ?? false;
  const { openCurrencyDialog } = useShippingAddressAndCurrencyDialogsContext();
  const [isCashFundCurrencyI18nEnabled] = useIsFeatureEnabled('registryCashFundCurrency18nEnabled');
  const showChangeCurrencyCta = !!creditCardSetupContext && isCashFundCurrencyI18nEnabled && !fetchingPayoutInfo && !payoutAccountInfo && !isEditDialog;
  const onChangeCurrencyClick = useCallback(() => {
    onChangeCurrencyCtaClicked();
    openCurrencyDialog();
  }, [onChangeCurrencyCtaClicked, openCurrencyDialog]);
  const resolvedRegistryCurrencyCode = useResolvedCurrencyCode(registryCurrency?.code);
  const registryCurrencyCodeAtMountRef = useRef<string>(resolvedRegistryCurrencyCode);

  const { getCashFundDialogTranslations, getMustHaveTranslations } = useSharedRegistryTranslations();
  const {
    nameEmptyError,
    quantityRequiredError,
    quantityIntegerError,
    amountRequiredError,
    selectPaymentEmptyError,
    invalidUsernameError,
    invalidUsernameIsEmailError,
    cashFundNameSuggestions,
    charityFundNameSuggestions,
    newQtyError,
    newAmountError,
    charityLinkInvalidError,
    charityLinkRequiredError,
    donationMethodOptionOneText,
    donationMethodOptionTwoText,
    anyAmountText,
    fixedAmountText,
  } = getCashFundDialogTranslations();
  const { canEnableCreditCard } = useCreditCardForCashEligibility();
  const donationFund = product?.donationFund;
  const qtyReceived = donationFund?.received || 0;
  const donationFundFundInfo = useMemo(() => product?.donationFund?.fund as DonationFundFragment | undefined, [product]);
  const currentPaymentMethod = donationFundFundInfo?.platform?.type || undefined;
  const currentUsername = donationFundFundInfo?.platform?.username || undefined;
  const isJoyCreditEnabled = isNewCashFund ? canEnableCreditCard : Boolean(donationFundFundInfo?.enableJoyCredit);
  const initialQtyValue = 6;
  const initialPrefixAmountOffset = getPrefixOffset(donationFundInfo?.defaultSuggestedDonationMonetaryValue?.valueInMinorUnits);
  const initialPrefixTotalOffset = getPrefixOffset(donationFundInfo?.defaultSuggestedDonationMonetaryValue?.valueInMinorUnits, initialQtyValue);
  const client = useApolloClient();
  const [createDonationFundFromExistingProductLoading, setCreateDonationFundFromExistingProductLoading] = useState(false);
  const [collectionMembers, setCollectionMembers] = useState<Maybe<ProductCatalogCollectionMembersFragment[]>>();
  const { currentUser } = useAuth();
  const {
    onOpenDisclaimerDialog,
    onClose: onCloseDisclaimerDialog,
    isOpen: isOpenDisclaimerDialog,
    disclaimerFormik,
    agreementAccepted,
    paymentMetadata,
    setAgreementAccepted,
  } = useAppPaymentDisclaimerDialogController(eventId);

  const { identify } = useIdentify();
  const { eventHandle } = useEventInfo();

  const guestSiteUrl = `${window.location.origin}/${eventHandle}/registry`;

  const isLinkToCharitySelectedByDefault = useMemo(() => {
    return !!(
      (isCharityFund && !donationFundFundInfo?.fundType && !donationFundFundInfo?.platform?.type) ||
      (isCharityFund &&
        (!isEditDialog || (donationFundFundInfo?.fundType === CustomItemDataType.charity && donationFundFundInfo?.platform?.type === DonationFundPlatformTypeEnum.other)))
    );
  }, [isCharityFund, isEditDialog, donationFundFundInfo]);

  const isAnyAmountSelectedByDefault = isEditDialog
    ? !donationFundFundInfo?.suggestedDonationMonetaryValue || !donationFundFundInfo.goalMonetaryValue?.valueInMinorUnits
    : !isCharityFund;
  const isGoalHiddenByDefault = !!donationFundFundInfo?.hideFundingGoal;

  const deriveFormFieldsFromProduct = useCallback(
    (cashFundProduct?: Maybe<CookedProduct | CookedProductCatalogItem>, updatedPhoto?: string | undefined) => {
      const cashFund = cashFundProduct?.donationFund?.fund as DonationFundFragment | undefined;
      const defaultPlatform = donationFundInfo?.defaultPlatform;
      const cashFundCurrency = cashFund?.currency?.code || registryCurrencyCodeAtMountRef.current;
      const charityLink = cashFund?.fundType === CustomItemDataType.charity ? cashFund?.platform?.link || donationFundInfo?.productUrl : '';
      return {
        enableCashOrCheck: Boolean(cashFund?.enableCashOrCheck),
        currency: getCurrencySymbolFromCurrencyCode(cashFundCurrency)!,
        qty: cashFundProduct?.requested || initialQtyValue,
        description: cashFund?.description || '',
        name: cashFundProduct?.title || '',
        image: cashFundProduct?.image || updatedPhoto || '',
        enableAppPlatform: !!cashFund?.enableAppPlatform,
        paymentMethod:
          cashFund?.platform?.type && cashFund?.platform?.type !== DonationFundPlatformTypeEnum.other
            ? cashFund?.platform?.type
            : defaultPlatform?.type !== DonationFundPlatformTypeEnum.other
              ? (defaultPlatform?.type as DonationFundPlatformTypeEnum)
              : undefined,
        username:
          cashFund?.platform?.type && cashFund?.platform?.type !== DonationFundPlatformTypeEnum.other && cashFund?.platform?.username
            ? cashFund?.platform?.username
            : defaultPlatform?.type !== DonationFundPlatformTypeEnum.other && defaultPlatform?.username
              ? (defaultPlatform?.username as string)
              : '',
        total: getMonetaryValueFromMinorUnits(cashFund?.goalMonetaryValue?.valueInMinorUnits),
        charityLink: isLinkToCharitySelectedByDefault && charityLink ? charityLink : '',
        isLinkToCharitySelected: isLinkToCharitySelectedByDefault,
        isAnyAmountSelected: isAnyAmountSelectedByDefault,
        isGoalHidden: isGoalHiddenByDefault,
        enableJoyCredit: isJoyCreditEnabled,
        mustHave: cashFundProduct?.mustHave || false,
        ...getInitialAmountAndCurrency(cashFundCurrency, isAnyAmountSelectedByDefault, cashFundProduct, donationFundFundInfo?.goalMonetaryValue?.valueInMinorUnits),
      };
    },
    [donationFundFundInfo, donationFundInfo, isAnyAmountSelectedByDefault, isGoalHiddenByDefault, isLinkToCharitySelectedByDefault, isJoyCreditEnabled],
  );

  const [
    {
      currentStep,
      reviewStep,
      formValues,
      amountPrefixOffset,
      isNameFieldFocused,
      namePlaceholderText,
      isDeleting,
      speed,
      loop,
      isSuccessStep,
      isCancelClicked,
      isLinkMousedOver,
      totalGoalErrorMessage,
      isPaymentMethodWarningShown,
      isLinkToCharitySelected,
      isAnyAmountSelected,
      isGoalHidden,
      showCatalogCompletionStep,
    },
    setState,
  ] = useImmer<State>(() => ({
    currentStep: 0,
    reviewStep: null,
    formValues: deriveFormFieldsFromProduct(product, updatedPhoto),
    amountPrefixOffset: initialPrefixAmountOffset,
    totalPrefixOffset: initialPrefixTotalOffset,
    isNameFieldFocused: false,
    namePlaceholderText: isCharityFund ? charityFundNameSuggestions[0] : cashFundNameSuggestions[0],
    isDeleting: true,
    speed: 100,
    loop: 0,
    isSuccessStep: false,
    isCancelClicked: false,
    isLinkMousedOver: false,
    totalGoalErrorMessage: undefined,
    isPaymentMethodWarningShown: false,
    isLinkToCharitySelected: isLinkToCharitySelectedByDefault,
    isAnyAmountSelected: isAnyAmountSelectedByDefault,
    isGoalHidden: isGoalHiddenByDefault,
    showCatalogCompletionStep: false,
  }));

  const { deriveCashFundPayload } = useCreateAddRegistryItemClickPayload();
  //const deriveTelemetryPage = useDeriveTelemetryPage();

  const { storedCuration } = useStoredCuration(product);

  const isTheLastDialogStep = () => {
    return currentStep === 3;
  };
  const isPaymentMethodStep = () => {
    return currentStep === 2;
  };

  const isTheFirstStep = () => {
    return currentStep === 0;
  };

  useEffect(() => {
    setState(draft => {
      draft.isLinkToCharitySelected = isLinkToCharitySelectedByDefault;
    });
  }, [setState, isLinkToCharitySelectedByDefault]);

  useEffect(() => {
    setState(draft => {
      draft.namePlaceholderText = isCharityFund ? charityFundNameSuggestions[0] : cashFundNameSuggestions[0];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCharityFund]);

  useEffect(() => {
    setState(draft => {
      draft.isAnyAmountSelected = isAnyAmountSelectedByDefault;
    });
  }, [setState, isAnyAmountSelectedByDefault]);

  useEffect(() => {
    if (dialogShown) {
      if (isSuccessStep) {
        cashFundDialogDetails.enter(5);
      } else if (isCancelClicked) {
        cashFundDialogDetails.enter(4);
      } else {
        cashFundDialogDetails.enter(currentStep);
      }
    }
  }, [dialogShown, currentStep, cashFundDialogDetails, isSuccessStep, isCancelClicked]);

  useEffect(() => {
    const prefixOffsetAmount = getPrefixOffset(donationFundInfo?.defaultSuggestedDonationMonetaryValue?.valueInMinorUnits);
    const prefixOffsetTotal = getPrefixOffset(donationFundInfo?.defaultSuggestedDonationMonetaryValue?.valueInMinorUnits, initialQtyValue);

    setState(draft => {
      draft.amountPrefixOffset = prefixOffsetAmount;
      draft.totalPrefixOffset = prefixOffsetTotal;
    });
  }, [setState, donationFundInfo]);

  const resetNamePlaceholderAnimation = () => {
    setState(draft => {
      draft.isNameFieldFocused = false;
      draft.loop = 0;
      draft.namePlaceholderText = isCharityFund ? charityFundNameSuggestions[0] : cashFundNameSuggestions[0];
    });
  };

  const timersRef = useRef<DialogTimers>({
    resetTriggerTimeout: undefined,
    placeholderAnimationTimeout: undefined,
    placeholderDeletingTimeout: undefined,
    closeDialogTimeout: undefined,
    resetFormTimeout: undefined,
    hideCatalogCompletion: undefined,
  });

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const timerIds = timersRef.current;
      Object.keys(timerIds).forEach(timerId => {
        if (timerId) {
          globalWindow.clearTimeout(timerIds[timerId as keyof typeof timerIds]);
        }
      });
    };
  }, []);

  const onResetController = () => {
    timersRef.current.resetTriggerTimeout = globalWindow.setTimeout(() => {
      setState(draft => {
        draft.currentStep = 0;
        draft.reviewStep = null;
        draft.isSuccessStep = false;
        draft.isLinkMousedOver = false;
        draft.amountPrefixOffset = initialPrefixAmountOffset;
        draft.totalPrefixOffset = initialPrefixTotalOffset;
        draft.isPaymentMethodWarningShown = false;
      });
      resetNamePlaceholderAnimation();
    }, 1000);

    formik.resetForm();
  };

  const handleCancel = () => {
    hideDialog();
    handleAbortCancel();
    formik.resetForm();
    onResetController();
  };

  const handleDeleteRegistryItem = async () => {
    if (!product || !product.id) {
      return;
    }
    await handleOnRemove(product);
    handleCancel();
  };

  const onCancelClick = () => {
    if (!isEmpty(formik.touched) && !isSuccessStep) {
      setState(draft => {
        draft.isCancelClicked = true;
        onChangeCashFundDialogState(4);
      });
    } else {
      handleCancel();
    }
  };

  const handleAbortCancel = () => {
    setState(draft => {
      draft.isCancelClicked = false;
    });
  };

  const handleConfirm = () => {
    handleSave();
    if (isCatalogDialog) {
      setState(draft => {
        draft.showCatalogCompletionStep = true;
      });
    }
  };

  const getExistingPlatform = (values: AddCashFundDialogFields) => {
    return !isLinkToCharitySelected || values.enableAppPlatform
      ? donationFundInfo?.platforms.find(platform => platform.type === values.paymentMethod && platform.username === values.username)
      : null;
  };

  const handleVerifyDisclaimerAgreement = () => {
    const existingPlatform = getExistingPlatform(formik.values);
    if (!existingPlatform && formik.values.enableAppPlatform && !agreementAccepted && currentUser.profile?.id) {
      onOpenDisclaimerDialog(createPaymentMetadata(formik.values));
    } else {
      onSaveClick();
    }
  };

  const onSaveClick = async () => {
    const errors = await formik.validateForm();
    if (isEmpty(errors) && !totalGoalErrorMessage) {
      await handleSave();
      if (isCatalogDialog) {
        setState(draft => {
          draft.showCatalogCompletionStep = true;
        });
      }
      onSaveRegistryItem(formik.values);
      identify({ addedCashFund: true });

      if (isNewCashFund && eventId) {
        onAddToRegistryClick(
          deriveCashFundPayload({
            method: addMethodType,
            page: deriveTelemetryPage({ product, selectedCategory, view: 'pdpModal' }),
            eventId,
            storedCuration,
            selectedCategory,
            itemPosition,
            qty: product?.requested || 1,
            product,
            customItemFormFields: null,
            itemType: isCharityFund ? CustomItemDataType.charity : CustomItemDataType.cash,
            metadataSource: getRegistryMetadataSource(),
          }),
        );
      }
      if (!isCatalogDialog) {
        handleCancel();
      }
    } else {
      const errorFields = Object.keys(errors);
      errorFields.map(errorField => {
        formik.setTouched({ ...formik.touched, [errorField]: true });
      });
    }
  };

  const onConfirmButtonClick = () => {
    if (isTheLastDialogStep()) {
      formik.validateForm().then((errors: FormikErrors<AddCashFundDialogFields>) => {
        if (isEmpty(errors)) {
          formik.setFieldValue('amount', Number(formik.values.amount));
          formik.setFieldValue('qty', Number(formik.values.qty));
          handleConfirm();
          setState(draft => {
            draft.isSuccessStep = true;
          });
          onChangeCashFundDialogState(5);
          identify({ addedCashFund: true });
          onAddToRegistryClick(
            deriveCashFundPayload({
              method: addMethodType,
              page: deriveTelemetryPage({ product, selectedCategory, view: 'pdpModal' }),
              eventId,
              storedCuration,
              selectedCategory,
              itemPosition,
              qty: formik.values.qty,
              product: null,
              customItemFormFields: formik.values,
              itemType: isCharityFund ? CustomItemDataType.charity : CustomItemDataType.cash,
              metadataSource: getRegistryMetadataSource(),
            }),
          );
        } else {
          const errorFields = Object.keys(errors);
          errorFields.map(errorField => {
            formik.setTouched({ ...formik.touched, [errorField]: true });
          });
        }
      });
    } else {
      formik.validateForm().then((errors: FormikErrors<AddCashFundDialogFields>) => {
        if (isEmpty(errors)) {
          if (formik.values.amount !== formValues.amount || formik.values.qty !== formValues.qty) {
            formik.setFieldValue('total', totalGoal);
          }
          if (updatedPhoto && updatedPhoto !== formValues.image) {
            formik.setFieldValue('image', updatedPhoto);
            setState(draft => {
              draft.formValues = {
                ...formValues,
                ...formik.values,
                image: updatedPhoto,
              };
            });
          } else {
            setState(draft => {
              draft.formValues = {
                ...formValues,
                ...formik.values,
              };
            });
          }
          if (reviewStep) {
            setState(draft => {
              draft.currentStep = reviewStep;
              draft.reviewStep = null;
            });
          } else {
            setState(draft => {
              draft.currentStep = currentStep + 1;
            });
          }
          onChangeCashFundDialogState(currentStep);
        } else {
          const errorFields = Object.keys(errors);
          errorFields.map(errorField => {
            formik.setTouched({ ...formik.touched, [errorField]: true });
          });
        }
      });
    }
  };

  const onReviewClick = (step: number) => {
    setState(draft => {
      draft.reviewStep = currentStep;
      draft.currentStep = step;
      draft.isLinkMousedOver = false;
    });
  };

  const onBackClick = () => {
    setState(draft => {
      draft.currentStep = draft.currentStep - 1;
    });
  };

  const onNameFieldKeyDown = () => {
    setState(draft => {
      draft.isNameFieldFocused = true;
    });
  };

  // typing animation
  const currentIndex: number = loop % (isCharityFund ? charityFundNameSuggestions.length : cashFundNameSuggestions.length);
  const cashFundNameSuggestion: string = isCharityFund ? charityFundNameSuggestions[currentIndex] : cashFundNameSuggestions[currentIndex];

  const handleTyping = useCallback(() => {
    if (dialogShown && !isNameFieldFocused) {
      setState(draft => {
        draft.namePlaceholderText = isDeleting
          ? cashFundNameSuggestion.substring(0, namePlaceholderText.length - 1)
          : cashFundNameSuggestion.substring(0, namePlaceholderText.length + 1);
        draft.speed = isDeleting ? 20 : 100;
      });

      if (!isDeleting && namePlaceholderText === cashFundNameSuggestion) {
        timersRef.current.placeholderDeletingTimeout = globalWindow.setTimeout(
          () =>
            setState(draft => {
              draft.isDeleting = true;
            }),
          500,
        );
      } else if (isDeleting && namePlaceholderText === '') {
        setState(draft => {
          draft.isDeleting = false;
          draft.loop = loop + 1;
        });
      }
    } else {
      setState(draft => {
        draft.namePlaceholderText = cashFundNameSuggestion;
      });
    }
  }, [cashFundNameSuggestion, isDeleting, loop, namePlaceholderText, isNameFieldFocused, setState, dialogShown]);

  useEffect(() => {
    timersRef.current.placeholderAnimationTimeout = globalWindow.setTimeout(() => {
      if (dialogShown) {
        handleTyping();
      }
    }, speed);
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      clearTimeout(timersRef.current.placeholderAnimationTimeout);
    };
  }, [dialogShown, handleTyping, speed]);
  // end typing animation

  const amountRef = useRef<HTMLInputElement>(null);
  const amountEl = amountRef.current;
  const totalRef = useRef<HTMLInputElement>(null);
  const totalEl = totalRef.current;

  const onAmountChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    formik.setFieldValue('amount', e.target.value);
    if (amountEl && totalEl) {
      amountEl.innerHTML = e.target.value;
      const amountElWidth = amountEl.offsetWidth;
      const totalElValue = getTotalGoalFormatted(Number(e.target.value), formik.values.qty);
      totalEl.innerHTML = totalElValue.toString();
      const totalElWidth = totalEl.offsetWidth;
      if (amountElWidth) {
        setState(draft => {
          draft.amountPrefixOffset = amountElWidth;
        });
      }
      if (totalElWidth) {
        setState(draft => {
          draft.totalPrefixOffset = totalElWidth;
        });
      }
    }
  };

  const onQtyChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    formik.setFieldValue('qty', e.target.value);
    if (totalEl) {
      const totalElValue = getTotalGoalFormatted(Number(e.target.value), formik.values.amount);
      totalEl.innerHTML = totalElValue.toString();
      const totalElWidth = totalEl.offsetWidth;
      setState(draft => {
        draft.totalPrefixOffset = totalElWidth;
      });
    }
  };

  const onQtyChangeEdit: React.ChangeEventHandler<HTMLInputElement> = e => {
    formik.setFieldValue('qty', e.target.value);
    const amount = formik.values.amount || 0;
    if (!isAnyAmountSelected && Number(e.target.value) * amount < qtyReceived) {
      setState(draft => {
        draft.totalGoalErrorMessage = newQtyError;
      });
    } else {
      if (totalGoalErrorMessage) {
        setState(draft => {
          draft.totalGoalErrorMessage = undefined;
        });
      }
    }
  };

  const onAmountChangeEdit: React.ChangeEventHandler<HTMLInputElement> = e => {
    formik.setFieldValue('amount', e.target.value);
    if (!isAnyAmountSelected && Number(e.target.value) * formik.values.qty < qtyReceived) {
      setState(draft => {
        draft.totalGoalErrorMessage = newAmountError;
      });
    } else {
      if (totalGoalErrorMessage) {
        setState(draft => {
          draft.totalGoalErrorMessage = undefined;
        });
      }
    }
  };

  const onUsernameChangeEdit: React.ChangeEventHandler<HTMLInputElement> = e => {
    formik.setFieldValue('username', e.target.value);
    if (e.target.value !== currentUsername && !!qtyReceived) {
      setState(draft => {
        draft.isPaymentMethodWarningShown = true;
      });
    } else if (isPaymentMethodWarningShown) {
      setState(draft => {
        draft.isPaymentMethodWarningShown = false;
      });
    }
  };

  const onNameFocus: React.ChangeEventHandler<HTMLInputElement> = () => {
    onCashFundDialogFieldEdit('title', currentStep);
  };

  const onUsernameFocus: React.ChangeEventHandler<HTMLInputElement> = () => {
    onCashFundDialogFieldEdit('username', currentStep);
  };

  const onDescriptionFocus: React.ChangeEventHandler<HTMLTextAreaElement> = () => {
    onCashFundDialogFieldEdit('description', currentStep);
  };

  const onAmountFocus: React.ChangeEventHandler<HTMLInputElement> = () => {
    if (!formik.values.amount) {
      formik.setFieldValue('amount', undefined);
    }
    onCashFundDialogFieldEdit('amount', currentStep);
  };

  const onQtyFocus: React.ChangeEventHandler<HTMLInputElement> = () => {
    onCashFundDialogFieldEdit('qty', currentStep);
  };

  const photoAnimationProps = useSpring(
    isSuccessStep
      ? {
          width: 190,
          height: 190,
          borderRadius: 100,
          marginTop: 80,
        }
      : {
          width: 64,
          height: 64,
          borderRadius: 8,
          marginTop: 0,
        },
  );

  const textAnimationProps = useSpring({ opacity: isSuccessStep ? 1 : 0 });

  const lastStepAnimationProps = useSpring<{ finalStep: number }>({ finalStep: isSuccessStep ? 0 : 1 });

  const theme = useTheme();

  const lastStepHeaderAnimationProps = useSpring({ backgroundColor: isSuccessStep ? theme.colors.white : theme.colors.mono2, height: isSuccessStep ? 575 : '' });

  const [createDonationFundPlatform] = useCreateDonationFundPlatformMutation();

  const [createDonationFund] = useCreateDonationFundMutation({
    refetchQueries: () => ['GetAdminRegistryData', 'GetAdminRegistryRegistries', 'RegistryGiftBasketCountByEventName'],
    awaitRefetchQueries: true,
  });

  const [updateDonationFund, { loading: updateDonationFundLoading }] = useUpdateDonationFundMutation({
    refetchQueries: () => ['GetAdminRegistryData', 'GetAdminRegistryRegistries', 'RegistryGiftBasketCountByEventName'],
  });

  const [uploadPhoto] = useAddDonationFundPhotoMutation({
    refetchQueries: () => ['GetAdminRegistryRegistries'],
  });

  const handleOnMouseEnter: MouseEventHandler<HTMLAnchorElement> = useEventCallback(_e => {
    setState(draft => {
      draft.isLinkMousedOver = true;
    });
  });

  const handleOnMouseLeave: MouseEventHandler<HTMLAnchorElement> = useEventCallback(_e => {
    setState(draft => {
      draft.isLinkMousedOver = false;
    });
  });

  /**
   * This method is being used instead the `useLazyQuery` generated hook because we need to fetch the data
   * alongside the `createDonationFund` mutation and only then move the user to the next step. Using
   * `useLazyQuery`'s `data` and `loading` states would cause the user to move to the next step before the
   * data is fetched.
   */
  const fetchProductCatalogCollectionDisplayInfos = useCallback(() => {
    if (product?.collectionDisplayInfos?.[0] === undefined) {
      return Promise.resolve();
    }

    return client.query<GetProductCatalogCollectionMembersQuery>({
      query: GetProductCatalogCollectionMembersDocument,
      variables: {
        collectionsRefList: [
          {
            displayInfoId: product?.collectionDisplayInfos?.[0]?.displayInfoId,
            pdpSectionType: product?.collectionDisplayInfos?.[0]?.pdpSectionType,
          },
        ],
      },
    });
  }, [client, product?.collectionDisplayInfos]);

  const handleCreateDonationFundFromExistingProduct = async (donationFundVariables: CreateDonationFundMutationVariables, _actions: FormikHelpers<AddCashFundDialogFields>) => {
    setCreateDonationFundFromExistingProductLoading(true);
    return Promise.all([createDonationFund({ variables: donationFundVariables }), fetchProductCatalogCollectionDisplayInfos()])
      .then(async ([createDonationFundResult, fetchProductCatalogCollectionDisplayInfosResults]) => {
        const data = createDonationFundResult.data?.createDonationFund;
        if (photoFilename && data?.id) {
          await uploadPhoto({ variables: { assetId: photoFilename, id: data.id } });
        }
        const productCatalogCollectionMembers = fetchProductCatalogCollectionDisplayInfosResults?.data?.productCatalogCollectionDisplayInfosByRefs?.[0]?.collection
          .members as ProductCatalogCollectionMembersFragment[];
        setCollectionMembers(productCatalogCollectionMembers);
        const shouldPreventDialogClose = productCatalogCollectionMembers?.length;
        return shouldPreventDialogClose;
      })
      .finally(() => {
        setCreateDonationFundFromExistingProductLoading(false);
      });
  };

  const handleSubmit = async (addCashFundDialogFields: AddCashFundDialogFields, actions: FormikHelpers<AddCashFundDialogFields>) => {
    const shouldSaveCharityLink = isCharityFund && isLinkToCharitySelected;
    const charityLink = addCashFundDialogFields.charityLink ? getClickableLink(addCashFundDialogFields.charityLink) : '';
    const normalizedUsername = normalizePaymentUsername(addCashFundDialogFields.paymentMethod, addCashFundDialogFields.username);
    const paymentLink = shouldSaveCharityLink ? charityLink : getPaymentLink(addCashFundDialogFields.paymentMethod, normalizedUsername);
    const platformType = shouldSaveCharityLink ? DonationFundPlatformTypeEnum.other : (addCashFundDialogFields.paymentMethod as DonationFundPlatformTypeEnum);
    const username = isLinkToCharitySelected || !addCashFundDialogFields.enableAppPlatform ? null : normalizedUsername;
    const metadataSource = getRegistryMetadataSource();
    const existingPlatform =
      !isLinkToCharitySelected || addCashFundDialogFields.enableAppPlatform
        ? findDonationFundPlatform({ paymentMethod: addCashFundDialogFields.paymentMethod, username: normalizedUsername }, paymentLink, donationFundInfo)
        : null;
    try {
      if (product?.id && !isCatalogDialog) {
        const productId = product.id;
        const platform = product.donationFund?.fund.platform;
        if (shouldSaveCharityLink || (addCashFundDialogFields.enableAppPlatform && !existingPlatform)) {
          const newPlatform = await createDonationFundPlatform({
            variables: {
              eventId: eventId,
              type: platformType,
              link: paymentLink,
              username: username,
            },
          });
          if (newPlatform) {
            const newPlatformData = newPlatform.data?.createDonationFundPlatform;
            await updateDonationFund({
              variables: {
                id: productId,
                platformId: newPlatformData?.id,
                title: addCashFundDialogFields.name,
                url: shouldSaveCharityLink || addCashFundDialogFields.enableAppPlatform ? (paymentLink as string) : '',
                imageUrlForImport: addCashFundDialogFields.image,
                description: addCashFundDialogFields.description,
                ...calculateDonationFundValuesFromFields(addCashFundDialogFields),
                enableAppPlatform: !shouldSaveCharityLink ? addCashFundDialogFields.enableAppPlatform : false,
                mustHave: addCashFundDialogFields.mustHave,
              },
            });
            if (photoFilename && product.image !== photoFilename) {
              await uploadPhoto({ variables: { assetId: photoFilename, id: productId } });
            }
          }
        } else {
          await updateDonationFund({
            variables: {
              id: productId,
              platformId: existingPlatform ? existingPlatform.id : platform?.id,
              title: addCashFundDialogFields.name,
              url: shouldSaveCharityLink || addCashFundDialogFields.enableAppPlatform ? (paymentLink as string) : '',
              imageUrlForImport: addCashFundDialogFields.image,
              description: addCashFundDialogFields.description,
              ...calculateDonationFundValuesFromFields(addCashFundDialogFields),
              enableAppPlatform: !shouldSaveCharityLink ? addCashFundDialogFields.enableAppPlatform : false,
              mustHave: addCashFundDialogFields.mustHave,
            },
          });
          if (photoFilename && (product.image !== photoFilename || !isCatalogDialog)) {
            await uploadPhoto({ variables: { assetId: photoFilename, id: productId } });
          }
        }
      } else {
        const donationFundValues = calculateDonationFundValuesFromFields(addCashFundDialogFields);
        const metadata: RegistryItemMetadata = {
          source: metadataSource,
          sourceMetadata: {
            scannedPrice: (donationFundValues.goalMonetaryMinorValue / 100).toString(),
          },
        };
        if (existingPlatform) {
          await handleCreateDonationFundFromExistingProduct(
            {
              eventId: eventId,
              platformId: existingPlatform.id,
              fundType: fundType,
              title: addCashFundDialogFields.name,
              url: existingPlatform.link ?? '',
              imageUrlForImport: addCashFundDialogFields.image,
              description: addCashFundDialogFields.description,
              ...donationFundValues,
              enableAppPlatform: !shouldSaveCharityLink ? addCashFundDialogFields.enableAppPlatform : false,
              metadata,
            },
            actions,
          );
        } else {
          let newPlatform;
          if (addCashFundDialogFields.enableAppPlatform || shouldSaveCharityLink) {
            newPlatform = await createDonationFundPlatform({
              variables: {
                eventId: eventId,
                type: platformType,
                link: paymentLink as string,
                username: username,
              },
            });
          }

          const newPlatformData = newPlatform ? newPlatform.data?.createDonationFundPlatform : undefined;

          await handleCreateDonationFundFromExistingProduct(
            {
              eventId: eventId,
              fundType: fundType,
              platformId: !newPlatformData ? null : newPlatformData?.id,
              title: addCashFundDialogFields.name,
              url: newPlatformData?.link ? newPlatformData.link : '',
              imageUrlForImport: addCashFundDialogFields.image,
              description: addCashFundDialogFields.description,
              ...donationFundValues,
              enableAppPlatform: !shouldSaveCharityLink ? addCashFundDialogFields.enableAppPlatform : false,
              metadata,
            },
            actions,
          );
        }
      }
    } catch (err) {
      addError(err, {
        context: 'AddCashFundDialog.handleSubmit',
        paymentMethod: addCashFundDialogFields.paymentMethod,
        isEditDialog: !!isEditDialog,
        isCharityFund,
        productId: product?.id ?? null,
        legacyPlatformLink: product?.donationFund?.fund.platform?.link ?? null,
      });
      throw err;
    }
  };

  const validationSchemaStep1 = Yup.object<AddCashFundDialogStep1>({
    isAnyAmountSelected: Yup.boolean(),
    qty: Yup.number().when('isAnyAmountSelected', {
      is: false,
      then: Yup.number().integer(quantityIntegerError).required(quantityRequiredError).min(1, quantityRequiredError),
      otherwise: Yup.number().integer(quantityIntegerError).min(1, quantityRequiredError),
    }),
    amount: Yup.number().when('isAnyAmountSelected', {
      is: false,
      then: Yup.number().required(amountRequiredError).min(1, amountRequiredError),
      otherwise: Yup.number().min(1, amountRequiredError),
    }),
    name: Yup.string().required(nameEmptyError),
    isGoalHidden: Yup.boolean(),
    mustHave: Yup.boolean(),
  });

  const validationSchema = [
    Yup.object<AddCashFundDialogStep0>({
      name: Yup.string().required(nameEmptyError),
    }),
    validationSchemaStep1,
    Yup.object<AddCashFundDialogStep2>({
      paymentMethod: Yup.mixed<DonationFundPlatformTypeEnum>().when('enableAppPlatform', {
        is: true,
        then: Yup.mixed<DonationFundPlatformTypeEnum>().required(selectPaymentEmptyError),
        otherwise: Yup.mixed<DonationFundPlatformTypeEnum>(),
      }),
      enableCashOrCheck: Yup.boolean().test(AT_LEAST_ONE_METHOD_TEST),
      enableJoyCredit: Yup.boolean().test(AT_LEAST_ONE_METHOD_TEST),
      enableAppPlatform: Yup.boolean().test(AT_LEAST_ONE_METHOD_TEST),
      username: Yup.string().when(['enableAppPlatform'], {
        is: true,
        then: Yup.string().matches(notEmailRegexp, { message: invalidUsernameIsEmailError }).required(invalidUsernameError),
        otherwise: Yup.string(),
      }),
    }),
    Yup.object<AddCashFundDialogStep3>({
      description: Yup.string().max(NOTE_MAX_CHARS),
    }),
  ];

  const validationSchemaEditDialog = Yup.object<AddCashFundDialogFields>({
    enableCashOrCheck: Yup.boolean().test(AT_LEAST_ONE_METHOD_TEST),
    enableJoyCredit: Yup.boolean().test(AT_LEAST_ONE_METHOD_TEST),
    enableAppPlatform: Yup.boolean().test(AT_LEAST_ONE_METHOD_TEST),
    name: Yup.string().required(nameEmptyError),
    isAnyAmountSelected: Yup.boolean(),
    qty: Yup.number().when('isAnyAmountSelected', {
      is: false,
      then: Yup.number().integer(quantityIntegerError).required(quantityRequiredError).min(1, quantityRequiredError),
      otherwise: Yup.number().integer(quantityIntegerError).min(1, quantityRequiredError),
    }),
    amount: Yup.number().when('isAnyAmountSelected', {
      is: false,
      then: Yup.number().required(amountRequiredError).min(1, amountRequiredError),
      otherwise: Yup.number().min(1, amountRequiredError),
    }),
    isGoalHidden: Yup.boolean(),
    paymentMethod: Yup.mixed<DonationFundPlatformTypeEnum>().when('enableAppPlatform', {
      is: true,
      then: Yup.mixed<DonationFundPlatformTypeEnum>().required(selectPaymentEmptyError),
      otherwise: Yup.mixed<DonationFundPlatformTypeEnum>(),
    }),
    username: Yup.string().when('enableAppPlatform', {
      is: true,
      then: Yup.string().matches(notEmailRegexp, { message: invalidUsernameIsEmailError }).required(invalidUsernameError),
      otherwise: Yup.string(),
    }),
    description: Yup.string().max(NOTE_MAX_CHARS),
    mustHave: Yup.boolean(),
  });

  const validationSchemaCharity = [
    Yup.object<AddCashFundDialogStep0>({
      name: Yup.string().required(nameEmptyError),
    }),
    validationSchemaStep1,
    Yup.object<AddCashFundDialogStep2>({
      isLinkToCharitySelected: Yup.boolean(),
      enableCashOrCheck: Yup.boolean().test(AT_LEAST_ONE_METHOD_TEST),
      enableJoyCredit: Yup.boolean().test(AT_LEAST_ONE_METHOD_TEST),
      enableAppPlatform: Yup.boolean().test(AT_LEAST_ONE_METHOD_TEST),
      paymentMethod: Yup.mixed<DonationFundPlatformTypeEnum>().when(['enableAppPlatform'], {
        is: true,
        then: Yup.mixed<DonationFundPlatformTypeEnum>().required(selectPaymentEmptyError),
        otherwise: Yup.mixed<DonationFundPlatformTypeEnum>(),
      }),
      username: Yup.string().when(['enableAppPlatform'], {
        is: true,
        then: Yup.string().matches(notEmailRegexp, { message: invalidUsernameIsEmailError }).required(invalidUsernameError),
        otherwise: Yup.string(),
      }),
      charityLink: Yup.string().when('isLinkToCharitySelected', {
        is: true,
        then: Yup.string()
          .required(charityLinkRequiredError)
          .test('is-valid-url-and-domain', charityLinkInvalidError, value => Boolean(value) && isValidUrlAndDomain(value)),
        otherwise: Yup.string(),
      }),
    }),

    Yup.object<AddCashFundDialogStep3>({
      description: Yup.string().max(NOTE_MAX_CHARS),
    }),
  ];

  const validationSchemaEditDialogCharity = Yup.object<AddCashFundDialogFields>({
    name: Yup.string().required(nameEmptyError),
    isAnyAmountSelected: Yup.boolean().test(AT_LEAST_ONE_METHOD_TEST),
    enableCashOrCheck: Yup.boolean().test(AT_LEAST_ONE_METHOD_TEST),
    enableJoyCredit: Yup.boolean().test(AT_LEAST_ONE_METHOD_TEST),
    enableAppPlatform: Yup.boolean().test(AT_LEAST_ONE_METHOD_TEST),
    qty: Yup.number().when('isAnyAmountSelected', {
      is: false,
      then: Yup.number().integer(quantityIntegerError).required(quantityRequiredError).min(1, quantityRequiredError),
      otherwise: Yup.number().integer(quantityIntegerError).min(1, quantityRequiredError),
    }),
    amount: Yup.number().when('isAnyAmountSelected', {
      is: false,
      then: Yup.number().required(amountRequiredError).min(1, amountRequiredError),
      otherwise: Yup.number().min(1, amountRequiredError),
    }),
    isGoalHidden: Yup.boolean(),
    isLinkToCharitySelected: Yup.boolean().test(AT_LEAST_ONE_METHOD_TEST),
    paymentMethod: Yup.mixed<DonationFundPlatformTypeEnum>().when(['enableAppPlatform'], {
      is: true,
      then: Yup.mixed<DonationFundPlatformTypeEnum>().required(selectPaymentEmptyError),
      otherwise: Yup.mixed<DonationFundPlatformTypeEnum>(),
    }),
    username: Yup.string().when(['enableAppPlatform'], {
      is: true,
      then: Yup.string().matches(notEmailRegexp, { message: invalidUsernameIsEmailError }).required(invalidUsernameError),
      otherwise: Yup.string(),
    }),
    charityLink: Yup.string().when('isLinkToCharitySelected', {
      is: true,
      then: Yup.string()
        .required(charityLinkRequiredError)
        .test('is-valid-url-and-domain', charityLinkInvalidError, value => Boolean(value) && isValidUrlAndDomain(value)),
      otherwise: Yup.string(),
    }),
    description: Yup.string().max(NOTE_MAX_CHARS),
    mustHave: Yup.boolean(),
  });

  const getValidationSchema = () => {
    if (isCharityFund) {
      return isEditDialog ? validationSchemaEditDialogCharity : validationSchemaCharity[currentStep];
    } else {
      return isEditDialog ? validationSchemaEditDialog : validationSchema[currentStep];
    }
  };

  const getRegistryMetadataSource = () => {
    const pageValue = deriveTelemetryPage({ product, selectedCategory, view: 'pdpModal' });
    return isEditDialog ? generateRegistryItemMetadataSourceFromCatalog(addMethodType, pageValue) : RegistryItemSource.manual;
  };

  const formik = useFormik<AddCashFundDialogFields>({
    enableReinitialize: true,
    initialValues: deriveFormFieldsFromProduct(product),
    onSubmit: handleSubmit,
    validationSchema: getValidationSchema(),
  });

  useSyncRegistryCurrencyToForm({
    isEditDialog,
    isNewCashFund,
    registryCurrencyCode: registryCurrency?.code,
    formCurrencyCode: formik.values.currencyCode,
    onCurrencyChange: (currencySymbol, currencyCode) => {
      formik.setFieldValue('currency', currencySymbol);
      formik.setFieldValue('currencyCode', currencyCode);
      setState(draft => {
        draft.formValues.currency = currencySymbol;
        draft.formValues.currencyCode = currencyCode;
      });
    },
  });

  useEffect(() => {
    const newPaymentMetadata = createPaymentMetadata(formik.values);
    if (
      agreementAccepted &&
      formik.values.enableAppPlatform &&
      (newPaymentMetadata.paymentMethod !== paymentMetadata?.paymentMethod || newPaymentMetadata.paymentUsername !== paymentMetadata.paymentUsername)
    ) {
      setAgreementAccepted(false);
    }
  }, [formik.values, setAgreementAccepted, agreementAccepted, paymentMetadata]);

  const onHandleDisclaimerStep = () => {
    const existingPlatform = getExistingPlatform(formik.values);
    const isStepPaymentMethodStep = isNewCashFund && isPaymentMethodStep();

    if (isStepPaymentMethodStep && currentUser.profile?.id && formik.values.enableAppPlatform && !existingPlatform && !agreementAccepted) {
      onOpenDisclaimerDialog(createPaymentMetadata(formik.values));
    } else {
      onConfirmButtonClick();
    }
  };

  const handleSave = async () => {
    if (formik.values.image !== updatedPhoto) {
      formik.setFieldValue('image', updatedPhoto);
    }
    return !formik.isSubmitting && formik.submitForm();
  };

  const setPaymentMethod = (paymentMethod?: DonationFundPlatformTypeEnum) => {
    formik.setFieldValue('paymentMethod', paymentMethod ? paymentMethod : undefined);

    onCashFundDialogFieldEdit('paymentMethod', currentStep);
    if (isEditDialog && paymentMethod !== currentPaymentMethod && !!qtyReceived) {
      setState(draft => {
        draft.isPaymentMethodWarningShown = true;
      });
    } else if (isPaymentMethodWarningShown) {
      setState(draft => {
        draft.isPaymentMethodWarningShown = false;
      });
    }
  };

  const selectedPaymentMethod: Maybe<PaymentMethodOption> = paymentMethodOptions.find(option => {
    return formik && formik.values && option.value === formik.values.paymentMethod && (formik.values.paymentMethod as string) !== DonationFundPlatformTypeEnum.other;
  });

  const getClickableLink = (link: string) => {
    return ensureHttpsPrefix(link);
  };

  // safari and firefox do not respect type="number" and pattern="[0-9]*"
  const ALLOWED_CHARS_REGEXP = /[0-9.]+/;

  const preventNonNumericalInput = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const keyCode = event.key;
    if (keyCode === 'Backspace' || keyCode === 'Delete' || keyCode === 'ArrowRight' || keyCode === 'ArrowLeft' || keyCode === 'Tab' || keyCode === 'Control') {
      return;
    } else if (!ALLOWED_CHARS_REGEXP.test(event.key)) {
      event.preventDefault();
    }
  };

  const totalGoal = getTotalGoalFormatted(formik.values.qty, formik.values.amount);

  const onPhotoClick = () => {
    openFilepicker();
    onCashFundDialogFieldEdit('photo', currentStep);
  };

  const onDonationMethodToggle = () => {
    setState(draft => {
      draft.isLinkToCharitySelected = !isLinkToCharitySelected;
    });
    formik.setFieldValue('isLinkToCharitySelected', !isLinkToCharitySelected);
  };

  const onHideGoalToggle = () => {
    const prevIsGoalHidden = isGoalHidden;
    setState(draft => {
      draft.isGoalHidden = !prevIsGoalHidden;
    });
    formik.setFieldValue('isGoalHidden', !prevIsGoalHidden);
  };

  const donationMethodTabs: SegmentedControllerItemProps[] = [
    {
      label: 'link',
      value: donationMethodOptionOneText,
      isActive: isLinkToCharitySelected,
      onClick: onDonationMethodToggle,
    },
    {
      label: 'platform',
      value: donationMethodOptionTwoText,
      isActive: !isLinkToCharitySelected,
      onClick: onDonationMethodToggle,
    },
  ];

  const onAmountToggle = () => {
    const prevIsAnyAmountSelected = isAnyAmountSelected;
    if (!isEditDialog && !formik.touched.amount) {
      if (isAnyAmountSelected) {
        formik.setFieldValue('amount', DEFAULT_FIXED_AMOUNT);
        setState(draft => {
          draft.amountPrefixOffset = getPrefixOffset(DEFAULT_FIXED_AMOUNT);
          draft.totalPrefixOffset = getPrefixOffset(DEFAULT_FIXED_AMOUNT, formik.values.qty);
        });
      } else {
        formik.setFieldValue('amount', DEFAULT_ANY_AMOUNT);
        setState(draft => {
          draft.amountPrefixOffset = getPrefixOffset(DEFAULT_ANY_AMOUNT);
        });
      }
    }
    setState(draft => {
      draft.isAnyAmountSelected = !prevIsAnyAmountSelected;
    });
    formik.setFieldValue('isAnyAmountSelected', !prevIsAnyAmountSelected);
  };

  const amountTabs: SegmentedControllerItemProps[] = [
    {
      label: 'any',
      value: anyAmountText,
      isActive: isAnyAmountSelected,
      onClick: onAmountToggle,
    },
    {
      label: 'fixed',
      value: fixedAmountText,
      isActive: !isAnyAmountSelected,
      onClick: onAmountToggle,
    },
  ];

  const onHelpIconClick = () => {
    globalWindow.open?.('https://help.withjoy.com/knowledge-base/cash-fund-goals-what-are-my-options');
  };

  const handleViewRegistry = () => {
    clickViewRegistry();
    handleCancel();
    withWindow(window => {
      window.open(guestSiteUrl, '_blank');
    });
  };

  const handleHoneymoonRemindMeLater = () => {
    onHoneymoonRemindMeLater({
      fundName: formik.values.name,
      productId: product?.id ?? null,
      selectedCategoryId: selectedCategory?.id ?? null,
      isNewCashFund: isNewCashFund ?? null,
    });
    handleCancel();
  };

  const handleHoneymoonStartExploring = () => {
    onHoneymoonStartExploring({
      fundName: formik.values.name,
      productId: product?.id ?? null,
      selectedCategoryId: selectedCategory?.id ?? null,
      isNewCashFund: isNewCashFund ?? null,
    });
  };

  const handleHoneymoonFundSuccessView = useCallback(() => {
    honeymoonFundSuccessView.enter();
  }, [honeymoonFundSuccessView]);

  const isUsdFund = [donationFundFundInfo?.currency?.code, registryCurrency?.code, resolvedRegistryCurrencyCode, formik.values.currencyCode]
    .filter(Boolean)
    .every(isUsdCurrencyCode);

  return {
    formik,
    isUsdFund,
    handleSave,
    isTheLastDialogStep,
    isTheFirstStep,
    photoAnimationProps,
    textAnimationProps,
    lastStepAnimationProps,
    lastStepHeaderAnimationProps,
    isSuccessStep,
    currentStep,
    reviewStep,
    formValues,
    amountRef,
    totalRef,
    amountPrefixOffset,
    namePlaceholderText,
    onReviewClick,
    onCancelClick,
    handleCancel,
    handleAbortCancel,
    onAmountChange,
    onQtyChange,
    setPaymentMethod,
    onConfirmButtonClick: onHandleDisclaimerStep,
    selectedPaymentMethod,
    paymentLink: getPaymentLink(formik.values.paymentMethod, formik.values.username),
    getClickableLink,
    onAmountFocus,
    isCancelClicked,
    onSaveClick: handleVerifyDisclaimerAgreement,
    preventNonNumericalInput,
    updateDonationFundLoading,
    NOTE_MAX_CHARS,
    isLinkMousedOver,
    handleOnMouseEnter,
    handleOnMouseLeave,
    onQtyChangeEdit,
    onAmountChangeEdit,
    totalGoalErrorMessage,
    isPaymentMethodWarningShown,
    onUsernameChangeEdit,
    totalGoal,
    onNameFocus,
    onUsernameFocus,
    onDescriptionFocus,
    onNameFieldKeyDown,
    onQtyFocus,
    onPhotoClick,
    onDonationMethodToggle,
    isLinkToCharitySelected,
    donationMethodTabs,
    isAnyAmountSelected,
    onAmountToggle,
    amountTabs,
    onHideGoalToggle,
    isGoalHidden,
    onHelpIconClick,
    showCatalogCompletionStep,
    handleDeleteRegistryItem,
    createDonationFundFromExistingProductLoading,
    collectionMembers,
    getMustHaveTranslations,
    trackEnableMustHave,
    handleViewRegistry,
    handleHoneymoonRemindMeLater,
    handleHoneymoonStartExploring,
    handleHoneymoonFundSuccessView,
    onBackClick,
    isOpenDisclaimerDialog,
    onCloseDisclaimerDialog,
    disclaimerFormik,
    canEnableCreditCard,
    showChangeCurrencyCta,
    onChangeCurrencyClick,
  };
};
